/* eslint-disable jira/integration/test-id-by-folder-structure */

/** @jsx jsx */
import React from 'react';
import { styled, jsx } from '@compiled/react';
import Button from '@atlaskit/button/button';
import Box from '@atlaskit/primitives/box';
import { token } from '@atlaskit/tokens';
import { css } from '@atlaskit/css';
import PeopleAndTasksIllustration from '@jira/illustrations__illustrations/src/ui/adg4/jira/heroes/components/people-and-tasks/index.tsx';
import { defineMessage, FormattedMessage } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useTeamPermissions } from './useTeamPermissions.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.p({
	color: token('color.text'),
	marginBottom: token('space.300'),
	marginTop: token('space.0'),
});

const imageWrapperStylesNew = css({
	marginBottom: token('space.200'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// @ts-expect-error - ts2322 - Type '{ borderRadius: "var(--ds-radius-medium)"; }' is not assignable to type 'undefined'.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		borderRadius: token('radius.medium', '6px'),
	},
});

const emptyStateDescriptionMessage = defineMessage({
	id: 'issue-field-team.empty-state-description',
	defaultMessage: 'Create a team to assign work across Jira and track it on your team’s profile',
	description: 'The description for the empty state of the teams field dropdown',
});

const createTeamButtonTextMessage = defineMessage({
	id: 'issue-field-team.create-team-button-text',
	defaultMessage: 'Create a team',
	description: 'The label of the inline Create team button in the teams field dropdown',
});

const noOptionsMessage = defineMessage({
	id: 'issue-field-team.no-options-message',
	defaultMessage: 'No options',
	description:
		'Message shown in the team picker dropdown when no matching teams are found for the current search query.',
});

type TeamPickerEmptyStateProps = {
	onActionClick: () => void;
};

export const TeamPickerEmptyState = ({ onActionClick }: TeamPickerEmptyStateProps): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { canCreateTeams } = useTeamPermissions();

	const handleCreateTeamOptionClick = () => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'createTeamInline', {
			teamsFieldEmptyState: true,
		});
		onActionClick();
	};

	return canCreateTeams ? (
		<Box
			paddingBlock="space.200"
			testId="issue-field-team.ui.edit-mode.team-picker-empty-state.empty-state"
		>
			<div css={imageWrapperStylesNew}>
				<PeopleAndTasksIllustration alt="" width={130} height={122} />
			</div>
			<Description>
				<FormattedMessage {...emptyStateDescriptionMessage} />
			</Description>
			<Button
				appearance="primary"
				onClick={handleCreateTeamOptionClick}
				testId="issue-field-team.ui.edit-mode.team-picker-empty-state.create-team-button"
				interactionName="auto-generated.issue-field-team.ui.edit-mode.team-picker-empty-state.create-team-button.button"
			>
				<FormattedMessage {...createTeamButtonTextMessage} />
			</Button>
		</Box>
	) : (
		<FormattedMessage {...noOptionsMessage} />
	);
};
